import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';

import type { SelectOption } from '@/components';
import type { BookingReservationDetail } from '@/types';
import type { MutationResponse } from '@/types/api/mutations';

import { BookingDialogBody, BookingDialogFooter, Input } from '@/components';
import { Form } from '@/components/Form';

import { useAppForm } from '@/hooks';

import { bookingSpecialistAvailabilityQueries } from '@/queries';

import { isSlotOffered, notifyBookingSuccess, toFormString } from '@/helpers';

import { SEND_DATES_FORMAT } from '@/constants/dates';

import { createRescheduleBookingReservationSchema } from '@/types/schemas';

import { rescheduleReservationPayload } from './utils';

type EditReservationFormProps = {
  reservation: BookingReservationDetail;
  reservationsUrl: string;
  specialistAvailabilityUrl: string;
  staff: SelectOption[];
  locations: SelectOption[];
  onClose: () => void;
  onSuccess: () => void;
};

export const EditReservationForm = (props: EditReservationFormProps) => {
  const {
    reservation,
    reservationsUrl,
    specialistAvailabilityUrl,
    staff,
    locations,
    onClose,
    onSuccess: propOnSuccess,
  } = props;
  const { t } = useTranslation();

  const onSuccess = (response: MutationResponse) => {
    notifyBookingSuccess(response);
    propOnSuccess();
    onClose();
  };

  const { form, formFields, onSubmit, isPending } = useAppForm({
    schema: createRescheduleBookingReservationSchema,
    defaultValues: {
      // Only a personal booking is rescheduled here, and one always names a specialist.
      staff_id: toFormString(reservation.staff?.id),
      location_id: toFormString(reservation.location.id),
      date: format(parseISO(reservation.starts_at), SEND_DATES_FORMAT),
      starts_at: reservation.starts_at,
      guest_info: toFormString(reservation.guest_info),
      note: toFormString(reservation.note),
      staff_comment: toFormString(reservation.staff_comment),
    },
    transform: (values) => rescheduleReservationPayload(values, reservation),
    entityId: `${reservation.id}/reschedule`,
    api_url: reservationsUrl,
    redirectUrl: '',
    onSuccess,
  });

  const [staffId, locationId, date, startsAt] = useWatch({
    control: form.control,
    name: ['staff_id', 'location_id', 'date', 'starts_at'],
  });

  const {
    data: availability,
    isLoading: isAvailabilityLoading,
    isFetching: isAvailabilityFetching,
  } = useQuery(
    bookingSpecialistAvailabilityQueries.slots({
      url: specialistAvailabilityUrl,
      locationId: Number(locationId) || null,
      staffId: Number(staffId) || null,
      serviceId: reservation.service.id,
      bookingType: 'personal',
      date,
      reservationId: reservation.id,
    }),
  );

  const slots = availability?.slots ?? [];

  const isStartUnfit =
    !isAvailabilityFetching && !!startsAt && !isSlotOffered(slots, startsAt);

  return (
    <>
      <BookingDialogBody>
        <p className="text-sm font-medium">{t('Edit booking')}</p>

        <Form
          form={form}
          onSubmit={onSubmit}
        >
          <formFields.Select
            name="staff_id"
            label={t('Trainer')}
            options={staff}
            isRequired
            searchable
          />
          <formFields.Select
            name="location_id"
            label={t('Location')}
            options={locations}
            isRequired
          />
          <formFields.DatePicker
            name="date"
            label={t('Date')}
            isRequired
          />

          <formFields.SlotPicker
            name="starts_at"
            label={t('Free start times')}
            slots={slots}
            isLoading={isAvailabilityLoading}
            isInvalid={isStartUnfit}
            isRequired
          />

          {reservation.client ? (
            <Input
              label={t('Client')}
              value={reservation.client.full_name}
              disabled
            />
          ) : (
            <formFields.Input
              name="guest_info"
              label={t('Guest name / phone')}
            />
          )}

          <formFields.Textarea
            name="note"
            label={t('Note')}
          />
          <formFields.Textarea
            name="staff_comment"
            label={t('Trainer comment')}
          />
        </Form>
      </BookingDialogBody>

      <BookingDialogFooter
        submitLabel={t('Save')}
        isPending={isPending}
        isSubmitDisabled={isStartUnfit || isAvailabilityFetching}
        onClose={onClose}
        onSubmit={onSubmit}
      />
    </>
  );
};
